import { Component } from '@theme/component';

/**
 * Announcement banner custom element that rotates between announcement content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [viewport]
 * @property {HTMLElement} [track]
 * @property {HTMLElement} [marqueeSource]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #marqueeFrame = 0;
  #marqueeObserver = new ResizeObserver(() => this.#queueMarquee());

  #queueMarquee = () => {
    cancelAnimationFrame(this.#marqueeFrame);
    this.#marqueeFrame = requestAnimationFrame(() => this.#buildMarquee());
  };

  #observeMarquee() {
    this.#marqueeObserver.disconnect();
    if (this.refs.viewport) this.#marqueeObserver.observe(this.refs.viewport);
    if (this.refs.marqueeSource) this.#marqueeObserver.observe(this.refs.marqueeSource);
    this.#queueMarquee();
  }

  #buildMarquee() {
    const { viewport, track, marqueeSource } = this.refs;
    if (!viewport || !track || !marqueeSource || !this.isConnected) return;
    track.querySelectorAll('[data-marquee-copy]').forEach((copy) => copy.remove());
    const width = marqueeSource.getBoundingClientRect().width;
    if (!width || !marqueeSource.children.length) return;
    const copies = Math.ceil(viewport.clientWidth / width) + 1;
    for (let i = 0; i < copies; i++) {
      const copy = /** @type {HTMLElement} */ (marqueeSource.cloneNode(true));
      copy.dataset.marqueeCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      for (const element of [copy, ...copy.querySelectorAll('*')]) {
        element.removeAttribute('ref');
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        if (element.matches('a, button, [tabindex]')) element.setAttribute('tabindex', '-1');
      }
      track.append(copy);
    }
    this.style.setProperty('--announcement-distance', `${width}px`);
    this.style.setProperty('--announcement-duration', `${width / (Number(this.dataset.marqueeSpeed) * 15 || 60)}s`);
    this.toggleAttribute('data-ready', true);
  }

  updatedCallback() {
    super.updatedCallback();
    this.suspend();
    if (this.dataset.marquee === 'true') this.#observeMarquee();
    else this.play();
  }

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.marquee === 'true') {
      this.#observeMarquee();
      return;
    }

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    this.#marqueeObserver.disconnect();
    cancelAnimationFrame(this.#marqueeFrame);

    if (this.pauseOnHover) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
    }

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.dataset.direction = 'next';
    this.current += 1;
  }

  previous() {
    this.dataset.direction = 'previous';
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.dataset.marquee === 'true') return;
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get pauseOnHover() {
    return this.getAttribute('pause-on-hover') !== 'false';
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
